import React, { useState, useEffect } from "react"
import { Layout, Menu, MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBook,
  faChevronLeft,
  faChevronRight,
  faCirclePlay,
  faCog,
  faFolder,
} from "@fortawesome/free-solid-svg-icons"
import { useNavigate } from "react-router-dom"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { KindeRole, KindeRolesClaim } from "types/kinde.types"
import { USER_ROLE } from "types/userRole.types"
import { useUiStore } from "stores/uiStore"

// Define the props interface
interface SideMenuProps {
  current?: string[]
}

// Define the menu item type
type MenuItem = Required<MenuProps>["items"][number]

/**
 * Represents the side menu component.
 * @param props - The component props
 * @returns  The JSX element representing the side menu component.
 */
const SideMenu: React.FC<SideMenuProps> = ({ current }) => {
  const { getClaim } = useKindeAuth()
  const [roles, setRoles] = useState<KindeRole[]>([])

  /**
   * Whether the icon rail is collapsed (hidden). Persisted across sessions via
   * the shared UI store and toggled from the floating edge handle.
   */
  const siderCollapsed = useUiStore((state) => state.siderCollapsed)
  const toggleSider = useUiStore((state) => state.toggleSider)

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const userRoles = (await getClaim("roles")) as KindeRolesClaim | null
        setRoles(userRoles?.value || [])
      } catch (error) {
        console.error("Failed to load user roles:", error)
        setRoles([])
      }
    }
    loadRoles()
  }, [getClaim])

  /**
   * Destructures Sider component from Layout.
   */
  const { Sider } = Layout
  const navigate = useNavigate()

  /**
   * Check if user has super admin privileges
   */
  const isSuperAdmin = roles.some(
    (role: KindeRole) => role.key === USER_ROLE.SUPER_ADMIN,
  )

  /**
   * An array of menu items for the side menu.
   */
  const sideMenuItems: MenuItem[] = [
    {
      /**
       * Key for the "home" menu item.
       */
      key: "/home",
      /**
       * Icon for the "home" menu item.
       */
      icon: (
        <FontAwesomeIcon
          widthAuto
          className="!text-3xl text-black"
          icon={faFolder}
        />
      ),
      /**
       * Click handler for the "home" menu item.
       */
      onClick: (): void => {
        navigate("/home")
      },
    },
    {
      /**
       * Key for the "home" menu item.
       */
      key: "/automation",
      /**
       * Icon for the "home" menu item.
       */
      icon: (
        <FontAwesomeIcon
          widthAuto
          className="!text-3xl text-black"
          icon={faCirclePlay}
        />
      ),
      /**
       * Click handler for the "home" menu item.
       */
      onClick: (): void => {
        navigate("/automation")
      },
    },
    {
      /**
       * Key for the "docs" menu item.
       */
      key: "/docs",
      /**
       * Icon for the "docs" menu item.
       */
      icon: (
        <FontAwesomeIcon
          widthAuto
          className="!text-3xl text-black"
          icon={faBook}
        />
      ),
      /**
       * Click handler for the "docs" menu item.
       */
      onClick: (): void => {
        navigate("/docs")
      },
    },
  ]

  // Add superadmin menu item if user has super admin privileges
  if (isSuperAdmin) {
    sideMenuItems.push({
      /**
       * Key for the "superadmin" menu item.
       */
      key: "/superadmin",
      /**
       * Icon for the "superadmin" menu item.
       */
      icon: (
        <FontAwesomeIcon
          widthAuto
          className="!text-3xl text-black"
          icon={faCog}
        />
      ),
      /**
       * Click handler for the "superadmin" menu item.
       */
      onClick: (): void => {
        navigate("/superadmin")
      },
    })
  }

  /**
   * Renders the side menu component.
   * @returns {JSX.Element} The JSX element representing the side menu.
   */
  return (
    <div className="sider-rail">
      <Sider
        width={80}
        collapsible
        trigger={null}
        collapsedWidth={0}
        collapsed={siderCollapsed}
      >
        <Menu
          mode="inline"
          defaultSelectedKeys={["/home"]}
          selectedKeys={current}
          className="side-nav h-full"
          items={sideMenuItems}
        />
      </Sider>
      <button
        type="button"
        className={`sider-edge-handle${
          siderCollapsed ? " sider-edge-handle--collapsed" : ""
        }`}
        aria-label="Toggle navigation"
        aria-expanded={!siderCollapsed}
        onClick={toggleSider}
      >
        <FontAwesomeIcon
          icon={siderCollapsed ? faChevronRight : faChevronLeft}
        />
      </button>
    </div>
  )
}

export default SideMenu
